/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Pagination, PaginationItem, PaginationLink, Form, FormGroup, Col, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import Toggle from 'react-toggle';
import Alert from 'react-s-alert';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';

// import services
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';

const ProviderForms = props => {

    // variables
    let timeout = 0;

    // states
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [isLoading3, setIsLoading3] = useState(false);
    const [forms, setForms] = useState([]);
    const [modal, setModal] = useState({ isVisible: false, isUpdate: false, selectedIndex: -1, name: "" });
    const [confirmationModal, setConfirmationModal] = useState({ isVisible: false, selectedIndex: -1 });
    const [pagination, setPagination] = useState({ limit: 10, page: 1 });

    // contexts
    const { theme } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        getForms();
    }, [pagination.page, pagination.limit, pagination.searchVal]);

    const getForms = () => {
        if (!isLoading) {
            setIsLoading(true);
            AdminService.getProviderForms(pagination.limit, pagination.page, pagination.searchVal).then(data => {
                try {
                    setForms(data.forms);
                    if (data.pagination.totalPages <= data.pagination.page) {
                        data.pagination.page = data.pagination.totalPages;
                    }
                    setPagination(data.pagination);
                    setIsLoading(false);
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    GeneralService.logError('at Admin Forms useEffect', 'Error in try catch block', err.stack);
                    setIsLoading(false);
                }
            });
        }
    }

    // handle functions
    const handleSearch = e => {
        try {
            let value = e.target.value;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                setPagination({ ...pagination, searchVal: value });
            }, 500);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleSearch', 'Error in try catch block', err.stack);
        }
    }

    const handlePagination = (e, index) => {
        e.preventDefault();
        try {
            setPagination({ ...pagination, page: index });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handlePagination', 'Error in try catch block', err.stack);
        }
    }

    const handleFormSave = () => {
        try {
            setIsLoading2(true);
            AdminService.addProviderForm({ name: modal.name }).then(data => {
                getForms();
                setModal({ isVisible: false, isUpdate: false, selectedIndex: -1, name: "" });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                setIsLoading2(false)
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormSave', 'Error in try catch block', err.stack);
        }
    }

    const handleFormUpdate = () => {
        try {
            setIsLoading2(true);
            AdminService.updateProviderForm({ id: forms[modal.selectedIndex]._id, name: modal.name }).then(data => {
                getForms();
                setModal({ isVisible: false, isUpdate: false, selectedIndex: -1, name: "" });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading2(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading2(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormUpdate', 'Error in try catch block', err.stack);
        }
    }

    const handleFormDelete = (id) => {
        try {
            setIsLoading3(true);
            AdminService.deleteProviderForm(id).then(data => {
                getForms();
                setConfirmationModal({ isVisible: false, id: "" });
                setModal({ isVisible: false, isUpdate: false, data: { ...modal.data, question: "", answers: [], orderNo: "" } });
                Alert.info(data.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading3(false);
            }).catch(error => {
                Alert.error(error.message.msgBody, {
                    position: 'top-right',
                    effect: 'slide'
                });
                isLoading3(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms handleFormDelete', 'Error in try catch block', err.stack);
        }
    }

    //utility functions
    const showQuestions = (index) => {
        try {
            props.setSelectedForm({ id: forms[index]._id, name: forms[index].name, mode: 'question' });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms showQuestions', 'Error in try catch block', err.stack);
        }
    }

    const showResponses = (index) => {
        try {
            props.setSelectedForm({ id: forms[index]._id, name: forms[index].name, mode: 'response' });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms showRespones', 'Error in try catch block', err.stack);
        }
    }

    const copyFormLink = (index) => {
        try {
            navigator.clipboard.writeText(`${window.location.host}/counsellorForm/${forms[index]._id}`).then(() => {
                Alert.info("Form link copied to clipboard.", {
                    position: 'top-right',
                    effect: 'slide'
                });
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Forms copyFormLink', 'Error in try catch block', err.stack);
        }
    }

    return (
        <main>
            <div className="container-fluid">
                <h3 className="my-4">Forms</h3>
                <div className="card mb-4">
                    <div className="card-header d-flex">
                        <label className="my-auto">Form List</label>
                        <Button className="ml-auto my-auto px-3" type="button" style={Object.assign({ height: 'fit-content' }, theme.formButton)} onClick={() => setModal({ isVisible: true, isUpdate: false, selectedIndex: -1, name: "" })}>Add</Button>
                    </div>
                    <div className="card-body py-0">
                        <div className="table-responsive">
                            <table className="table table-bordered mb-0" id="dataTable" width="100%" cellSpacing="0">
                                <thead>
                                    <tr>
                                        <td colSpan="6" className="border-0 px-0">
                                            <div className="d-flex">
                                                <div className="ml-auto">
                                                    Search<input type="text" id="searchInput" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input>
                                                    <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput").value = ""; setPagination({ ...pagination, searchVal: "" }); }}><i className="fa fa-times"></i></span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Name</th>
                                        <th align="center" style={{ textAlign: "center" }} colSpan="5">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        forms.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Forms Table${index}`}>
                                                <tr key={index}>
                                                    <td>{item.name}</td>
                                                    <td align="center" style={{ width: "1px" }}><span style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: true, isUpdate: true, selectedIndex: index, name: item.name })}><i className="fas fa-edit"></i></span></td>
                                                    <td align="center" style={{ width: "1px" }}><span style={{ cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: true, selectedIndex: index })}><i className="far fa-trash-alt"></i></span></td>
                                                    <td align="center" style={{ width: '1px' }}><a style={{ color: "#384E73" }} href="#" onClick={() => showQuestions(index)}>View Questions</a></td>
                                                    <td align="center" style={{ width: '1px' }}><a style={{ color: "#384E73" }} href="#" onClick={() => showResponses(index)}>View Responses</a></td>
                                                    <td align="center" style={{ width: '1px' }}><a style={{ color: "#384E73" }} href="#" onClick={() => copyFormLink(index)}>Copy Form Link</a></td>
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="6" className="border-0 pb-0 px-0">
                                            <div className="d-flex">
                                                <ErrorBoundary errorAt="At Admin Forms Pagination">
                                                    <div className="p-2">Showing {pagination.totalResults == 0 ? 0 : (1 + (10 * (pagination.page - 1)))} to {(pagination.limit + (10 * (pagination.page - 1))) > pagination.totalResults ? pagination.totalResults : (pagination.limit + (10 * (pagination.page - 1)))} of {pagination.totalResults} entries</div>
                                                    <Pagination aria-label="Page navigation example" className="ml-auto">
                                                        <PaginationItem disabled={pagination.page == 1}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page - 1)}
                                                                previous
                                                                href="#" />
                                                        </PaginationItem>
                                                        {[...Array(pagination.totalPages)].map((page, i) =>
                                                            <PaginationItem active={i + 1 === pagination.page} key={i}>
                                                                <PaginationLink onClick={e => handlePagination(e, i + 1)} href="#">
                                                                    {i + 1}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        )}
                                                        <PaginationItem disabled={pagination.page == pagination.totalPages}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page + 1)}
                                                                next
                                                                href="#" />
                                                        </PaginationItem>
                                                    </Pagination>
                                                </ErrorBoundary>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <ErrorBoundary errorAt="At Admin Forms ConfirmationModal">
                <Modal size="sm" isOpen={confirmationModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setConfirmationModal({ isVisible: false, selectedIndex: -1 })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Are you sure?</span></ModalHeader>
                    <ModalBody>
                        {
                            confirmationModal.selectedIndex >= 0 ?
                                <Label>By clicking on Save button <b>{forms[confirmationModal.selectedIndex].name}</b> will be <b>Deleted</b>.</Label>
                                : null
                        }
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleFormDelete(forms[confirmationModal.selectedIndex]._id)} disabled={isLoading3}>
                            {isLoading3 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
            <ErrorBoundary errorAt="At Admin Forms Modal">
                <Modal size="sm" isOpen={modal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: false, isUpdate: false, selectedIndex: -1, formName: "" })}><i className="fas fa-times"></i></span>}>
                        <span style={theme.headingBold}>{modal.isUpdate ? "Update form" : "Add form"}</span>
                    </ModalHeader>
                    <ModalBody>
                        <Label>Enter form name</Label>
                        <Input type="text" value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} />
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => modal.isUpdate ? handleFormUpdate() : handleFormSave()} disabled={isLoading2}>
                            {isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}{modal.isUpdate ? "Update" : "Save"}
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
        </main>
    )

}

export default ProviderForms;